/* eslint-disable @typescript-eslint/no-unused-vars */
import { z } from "zod";
import { Alert, Button, Group, Modal, Stack, Switch, TextInput } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import { nameSchema } from "../../../utils/schema";
import { useForm } from "@mantine/form";
import { IconExclamationCircle } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import type { Account } from "../Accounts";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface AddAccountModalProps {
  opened: boolean;
  onClose: () => void;
  account: Account;
}

export const EditAccountModal: FC<AddAccountModalProps> = ({ opened, onClose, account }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mapkaNotification = useMapkaNotification();

  const { mutateAsync, isPending, error } = useMutation(trpc.updateAccount.mutationOptions());

  const validate = useMemo(() => zod4Resolver(
    z.object({
      id: z.number(),
      name: nameSchema(intl),
      displayName: z.string(),
      sendInvoicesAttachments: z.boolean(),
      isDevelopmentPartner: z.boolean(),
      allowSupportEdit: z.boolean(),
    }),
  ), [intl]);

  const { getInputProps, onSubmit, setValues, isValid, reset, values } = useForm({
    validateInputOnBlur: true,
    initialValues: account,
    validate,
  });

  useEffect(() => {
    setValues(account);
  }, [account]);

  const handleSubmit = useCallback((form: typeof values) => {
    void mutateAsync(form)
      .then(() => {
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Account updated",
            id: "notification.accountUpdated",
          }),
          message: intl.formatMessage({
            defaultMessage: "Account {name} has been updated successfully",
            id: "notification.accountUpdatedMessage",
          },
          { name: form.name },
          ),
          variant: "success",
        });
        void queryClient
          .invalidateQueries(trpc.getAccounts.queryFilter())
          .then(() => {
            reset();
            onClose();
          });
      });
  }, [mutateAsync, onClose, mapkaNotification, intl, queryClient, trpc, reset]);

  let errorMessage: ReactNode = null;
  if (error) {
    if (error.message.includes("Email is disposable")) {
      errorMessage = (
        <FormattedMessage
          id="error.disposableEmail"
          defaultMessage="Disposable email addresses are not allowed"
        />
      );
    }
    else {
      errorMessage = error.message;
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title={intl.formatMessage({ defaultMessage: "Update account", id: "title.updateAccount" })}>
      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap="xs">
          {error && (
            <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
              {errorMessage}
            </Alert>
          )}
          <>
            <TextInput
              required
              flex={1}
              autoComplete="name"
              label={<FormattedMessage id="label.name" defaultMessage="Name" />}
              {...getInputProps("name")}
            />
            <TextInput
              flex={1}
              autoComplete="displayName"
              label={<FormattedMessage id="label.displayName" defaultMessage="Display Name" />}
              {...getInputProps("displayName")}
            />
          </>
          <TextInput
            required
            flex={1}
            autoComplete="billingEmail"
            label={<FormattedMessage id="label.billingEmail" defaultMessage="Billing Email" />}
            {...getInputProps("billingEmail")}
          />

          <Switch
            py="xs"
            label={<FormattedMessage id="label.isDevelopmentPartner" defaultMessage="Is development partner" />}
            {...getInputProps("isDevelopmentPartner", { type: "checkbox" })}
          />

          <Switch
            py="xs"
            label={<FormattedMessage id="label.allowSupportEdit" defaultMessage="Allow support edit" />}
            {...getInputProps("allowSupportEdit", { type: "checkbox" })}
          />

          <Switch
            py="xs"
            label={<FormattedMessage id="label.sendInvoicesAttachments" defaultMessage="Send invoices attachments" />}
            {...getInputProps("sendInvoicesAttachments", { type: "checkbox" })}
          />

          <Group gap="xs" mt="xs" justify="flex-end">
            <Button variant="secondary" type="reset" onClick={onClose}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="tertiary" type="submit" loading={isPending} disabled={!isValid()}>
              <FormattedMessage id="button.update" defaultMessage="Update" />
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
